import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildManualBankAccountFormRows,
  buildAccountHolderFormRows,
  buildAccountDetailsFormRows,
  buildAdditionalDetailsFormRows,
} from './buildManualBankAccountForm.helper';
import type { ManualBankAccountHandler } from './types';
import { useBank } from '../../../../providers/Bank.provider';
import { useToken } from '../../../../providers/Token.provider';
import { FieldData } from '../../../Form/types';
import { useForm } from '../../../Form/utils';
import styles from './ManualBankAccount.module.scss';
import {
  Box,
  SubTitle,
  Accordion,
  withStyles,
  NumbersOnly,
  LinkBankAccountManuallyRequest,
  FinancialInstitutionBranchesResponse,
} from 'vopay';

const classes = withStyles(styles);

type ManualBankAccountFormProps = {
  defaultValues: LinkBankAccountManuallyRequest;
  financialInstitutionBranches: FinancialInstitutionBranchesResponse;
  BankId: number;
  handlers: ManualBankAccountHandler;
  shouldConnectBank: boolean;
  isFormCompleted: boolean;
  setIsFormCompleted: (val: boolean) => void;
};

const ManualBankAccountForm: FunctionComponent<ManualBankAccountFormProps> = ({
  defaultValues,
  handlers,
  financialInstitutionBranches,
  BankId,
  shouldConnectBank,
  isFormCompleted,
  setIsFormCompleted,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'ManualBank' });
  const { token } = useToken();
  const { AccountType, FinancialInstitution } = useBank();
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const ManualBankAccountFormRows = buildManualBankAccountFormRows(financialInstitutionBranches, BankId);
  const ManualBankAccountDetailsFormRows = buildAccountHolderFormRows();
  const AccountDetailsFormRows = buildAccountDetailsFormRows();
  const AdditionalDetailsRows = buildAdditionalDetailsFormRows();
  const isBankTypeKnown = token?.AccountSelectionMethod !== 'any' && token?.AccountHolderType !== '';

  const fieldDataList: FieldData<LinkBankAccountManuallyRequest>[] = [
    ...ManualBankAccountFormRows,
    ...ManualBankAccountDetailsFormRows,
    ...AdditionalDetailsRows,
    ...AccountDetailsFormRows,
  ];

  const onSubmit = (values: LinkBankAccountManuallyRequest) => {
    handlers.linkBankAccount({
      ...values,
      PhoneNumber: NumbersOnly(values.PhoneNumber || ''),
    });
  };

  const { renderFormElements, ctx } = useForm<LinkBankAccountManuallyRequest, LinkBankAccountManuallyRequest>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData) => onSubmit(serializedData),
  });

  useEffect(() => {
    if (shouldConnectBank && isFormCompleted) {
      onSubmit({
        ...ctx.values,
        FirstName: AccountType === undefined || AccountType === 'personal' ? ctx.values.FirstName || '' : '',
        LastName: AccountType === undefined || AccountType === 'personal' ? ctx.values.LastName || '' : '',
        CompanyName: AccountType === 'business' ? ctx.values.CompanyName || '' : '',
      });
    }
  }, [shouldConnectBank]);

  useEffect(() => {
    ctx.validateForm();
  }, [ctx.values, AccountType]);

  useEffect(() => {
    if (token?.Country === 'US') {
      ctx.setValues(defaultValues);
    }
  }, [token?.Country, FinancialInstitution]);

  useEffect(() => {
    setIsFormCompleted(ctx.isValid);
  }, [ctx.isValid]);

  useEffect(() => {
    ctx.setFieldValue('FinancialInstitutionNumber', FinancialInstitution?.FinancialInstitutionNumber);
    ctx.setFieldValue('RoutingNumber', '');
  }, [FinancialInstitution]);

  const hasBankDetails =
    ctx.values.AccountNumber !== undefined ||
    ctx.values.BranchTransitNumber !== undefined ||
    ctx.values.RoutingNumber !== undefined;

  const provideBank = () => (
    <>
      {!isAccordionOpen && hasBankDetails ? (
        <Box flx="col" gapSize="4px" height="auto" pt="sm">
          {ctx.values.AccountNumber && (
            <div className={classes('bankInformation')}>
              <span>{t('accountNumber')}:</span>
              <span>{ctx.values.AccountNumber}</span>
            </div>
          )}
          {ctx.values.RoutingNumber && (
            <div className={classes('bankInformation')}>
              <span>{t('aba')}:</span>
              <span>{ctx.values.RoutingNumber}</span>
            </div>
          )}
          {ctx.values.BranchTransitNumber && (
            <div className={classes('bankInformation')}>
              <span>{t('branchTransitNumber')}:</span>
              <span>{ctx.values.BranchTransitNumber}</span>
            </div>
          )}
        </Box>
      ) : null}
    </>
  );

  return (
    <form onSubmit={ctx.handleSubmit}>
      <Box flx="col" gapSize="24px">
        <Accordion
          title={`${isBankTypeKnown ? '2' : '3'}. ${t('provideDetailsTitle')}`}
          iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
          iconSize="xsm"
          variant="box"
          titlePadding="0px"
          open={isAccordionOpen}
          onClick={() => {
            setIsAccordionOpen(!isAccordionOpen);
          }}
          subBody={provideBank()}
          body={
            <>
              <div
                className={classes(
                  'accountFormContainer',
                  Object.keys(financialInstitutionBranches.Branches).length >= 1 && 'noInstitutionNum',
                  token?.Country === 'US' && 'usAccount',
                )}
              >
                {renderFormElements({ config: ManualBankAccountFormRows })}
              </div>

              <Box pt="sm" pb="sm" height="auto">
                <SubTitle fontSize="16px" fontWeight="400" subTitle={`${t('manualBankSubTitle')}`} />
              </Box>
              <Box flx="col" gap="sm">
                <div className={classes('accountHolderContainer', AccountType === 'business' && 'business')}>
                  {renderFormElements({ config: ManualBankAccountDetailsFormRows })}
                </div>
                <div className={classes('accountHolderContainer', AccountType === 'business' && 'business')}>
                  {renderFormElements({ config: AdditionalDetailsRows })}
                </div>
              </Box>
            </>
          }
        />

        {token?.Country === 'US' && (
          <Accordion
            title={`${isBankTypeKnown ? '3' : '4'}. ${t('provideAccountDetailsTitle')}`}
            iconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
            iconSize="xsm"
            variant="box"
            titlePadding="0px"
            open={isAccordionOpen}
            onClick={() => {
              setIsAccordionOpen(!isAccordionOpen);
            }}
            body={
              <div className={classes('accountDetailsContainer')}>
                {renderFormElements({ config: AccountDetailsFormRows })}
              </div>
            }
          />
        )}
      </Box>
    </form>
  );
};

export default ManualBankAccountForm;
